import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { scoreRangeForTiers } from './constants';

/**
 * Owns all cohort filter state and produces a single canonical `payload` object that
 * both callers (preview, send) consume. Eliminates the duplicated filter-construction
 * blocks in the previous version.
 *
 * Also handles the tier→score auto-snap effect with a suppression flag, used when
 * loading a past broadcast's explicit score range back into the builder.
 */
export const useBroadcastFilters = () => {
  const [tiers, setTiers] = useState([]);
  const [score, setScore] = useState([0, 100]);
  const [shopperIds, setShopperIds] = useState([]);
  const [noirInteraction, setNoirInteraction] = useState('any');
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [codeLogic, setCodeLogic] = useState('any');
  const [noirEngageWindow, setNoirEngageWindow] = useState('');
  const [noirEngageType, setNoirEngageType] = useState('any');
  const [purchaseFilter, setPurchaseFilter] = useState('any');
  const [curatorIds, setCuratorIds] = useState([]);
  const [circleCuratorIds, setCircleCuratorIds] = useState([]);
  const [brandIds, setBrandIds] = useState([]);
  const [userType, setUserType] = useState('both');
  const [onWaitlist, setOnWaitlist] = useState(false);
  // Day-window for the recent-message exclusion, as the raw input string ('' = no exclusion).
  const [excludeRecentDays, setExcludeRecentDays] = useState('');

  // Set true for one render cycle when loading a past broadcast's filters into the builder.
  // Prevents the tier-snap effect from clobbering the broadcast's explicit score range.
  const suppressTierSnap = useRef(false);

  useEffect(() => {
    if (suppressTierSnap.current) {
      suppressTierSnap.current = false;
      return;
    }
    setScore(scoreRangeForTiers(tiers));
  }, [tiers]);

  const hasFilters = Boolean(
    tiers.length > 0 ||
      shopperIds.length > 0 ||
      noirInteraction !== 'any' ||
      score[0] > 0 ||
      score[1] < 100 ||
      selectedCodes.length > 0 ||
      noirEngageWindow ||
      noirEngageType !== 'any' ||
      purchaseFilter !== 'any' ||
      curatorIds.length > 0 ||
      circleCuratorIds.length > 0 ||
      brandIds.length > 0 ||
      userType !== 'both' ||
      onWaitlist
  );

  const payload = useMemo(
    () => ({
      tiers,
      scoreMin: score[0],
      scoreMax: score[1],
      userIds: shopperIds,
      noirInteraction,
      noirEngageWindow: noirEngageWindow ? parseInt(noirEngageWindow, 10) : null,
      noirEngageType,
      purchaseFilter,
      curatorIds,
      circleCuratorIds,
      brandIds,
      userType,
      onWaitlist,
      selectedCodes,
      codeLogic,
      excludeRecentDays: parseInt(excludeRecentDays, 10) > 0 ? parseInt(excludeRecentDays, 10) : null
    }),
    [
      tiers,
      score,
      shopperIds,
      noirInteraction,
      noirEngageWindow,
      noirEngageType,
      purchaseFilter,
      curatorIds,
      circleCuratorIds,
      brandIds,
      userType,
      onWaitlist,
      selectedCodes,
      codeLogic,
      excludeRecentDays
    ]
  );

  // Reset every filter (including the recent-message exclusion) back to its initial value —
  // the "Clear all" action in the cohort builder. The tier-snap effect re-fires on setTiers([])
  // but resolves to the same [0, 100] default, so no suppression is needed.
  const resetFilters = useCallback(() => {
    setTiers([]);
    setScore([0, 100]);
    setShopperIds([]);
    setNoirInteraction('any');
    setSelectedCodes([]);
    setCodeLogic('any');
    setNoirEngageWindow('');
    setNoirEngageType('any');
    setPurchaseFilter('any');
    setCuratorIds([]);
    setCircleCuratorIds([]);
    setBrandIds([]);
    setUserType('both');
    setOnWaitlist(false);
    setExcludeRecentDays('');
  }, []);

  // Hydrate from a past broadcast's filter snapshot (duplicate flow). Uses suppressTierSnap so the
  // snapshot's explicit score range isn't immediately overwritten by the tier-snap effect.
  const loadFromSavedFilters = useCallback(f => {
    suppressTierSnap.current = true;
    setTiers(f?.tiers || []);
    setScore([f?.scoreMin ?? 0, f?.scoreMax ?? 100]);
    setShopperIds(Array.isArray(f?.userIds) ? f.userIds.map(n => parseInt(n, 10)).filter(Boolean) : []);
    setNoirInteraction(f?.noirInteraction || 'any');
    setNoirEngageWindow(f?.noirEngageWindow ? String(f.noirEngageWindow) : '');
    setNoirEngageType(f?.noirEngageType || 'any');
    setPurchaseFilter(f?.purchaseFilter || 'any');
    setCuratorIds(f?.curatorIds || []);
    setCircleCuratorIds(f?.circleCuratorIds || []);
    setBrandIds(f?.brandIds || []);
    setUserType(f?.userType || 'both');
    setOnWaitlist(!!f?.onWaitlist);
    setSelectedCodes(f?.selectedCodes || []);
    setCodeLogic(f?.codeLogic || 'any');
    // Older snapshots stored the boolean excludeRecent14Days; map it onto the 14-day default.
    setExcludeRecentDays(f?.excludeRecentDays ? String(f.excludeRecentDays) : f?.excludeRecent14Days ? '14' : '');
  }, []);

  return {
    // Individual values + setters — exposed for direct binding in the cohort builder UI
    tiers,
    setTiers,
    score,
    setScore,
    shopperIds,
    setShopperIds,
    noirInteraction,
    setNoirInteraction,
    selectedCodes,
    setSelectedCodes,
    codeLogic,
    setCodeLogic,
    noirEngageWindow,
    setNoirEngageWindow,
    noirEngageType,
    setNoirEngageType,
    purchaseFilter,
    setPurchaseFilter,
    curatorIds,
    setCuratorIds,
    circleCuratorIds,
    setCircleCuratorIds,
    brandIds,
    setBrandIds,
    userType,
    setUserType,
    onWaitlist,
    setOnWaitlist,
    excludeRecentDays,
    setExcludeRecentDays,
    // Derived
    hasFilters,
    payload,
    // Side-effect helpers
    loadFromSavedFilters,
    resetFilters
  };
};
